/*
 * The Coming Soon gate.
 *
 * Coming Soon is a Patron benefit. Access itself is still open while the
 * catalogue is being prepared, so this changes what the play button does before
 * the entitlement changes: everybody but a Patron gets an explanation and a way
 * to become one, rather than silence now and a locked door later.
 *
 * It intercepts rather than replaces. The player is a compiled bundle that
 * binds its own click handler to .opera-player-launch, and editing that bundle
 * to add a membership rule would put a business decision inside a vendored
 * file. A capture-phase listener runs first and can simply decline to let the
 * click through.
 *
 * The tier comes from the same place and the same cache the price display uses,
 * so on a page where prices have already resolved this costs nothing.
 */
(function () {
  "use strict";

  var TIER_ENDPOINT = "/apps/library/membership/me";
  var TIER_CACHE_KEY = "od-member-tier-v1";
  /* Its own key: the tier cache is shared with the price display, which knows
     nothing about Coming Soon and would drop a field it did not write. */
  var ACCESS_CACHE_KEY = "od-coming-soon-access-v1";
  var TIER_CACHE_TTL_MS = 5 * 60 * 1000;

  var tier = null; // null = not known yet

  /*
   * What this visitor's tier may do with Coming Soon, straight from the
   * playback policy: "none" means offer them a Patron membership, "lossless"
   * means let it play. Null means we have not been told, and an untold answer
   * lets the click through — the app enforces the real entitlement when the
   * player asks for a token, so the worst case is a locked door appearing one
   * step later instead of one step too early.
   *
   * This used to be decided here, as "is this visitor a Patron". That was a
   * second copy of a rule the policy already held, and the two had drifted:
   * the policy gives a Collector no Coming Soon access, and the gate let
   * Collectors straight through.
   */
  var comingSoonAccess = null;
  var modal = null;
  var resolvedAt = 0;

  /* How stale a resolved tier may be before we look again when the page comes
     back into view. Short, because the case it exists for is somebody who has
     just paid. */
  var REFRESH_AFTER_MS = 20 * 1000;

  /*
   * Only a Coming Soon recording, and nothing else that plays.
   *
   * The second check used to be closest("[data-od-coming-soon]"), which
   * matches on the attribute being *present*. <html> always carries it —
   * "0" on an ordinary page — so every play button in the shop resolved to
   * Coming Soon and the Patron offer opened on free streaming, on product
   * pages, everywhere. The attribute has to be read, not merely found.
   */
  function isComingSoonContext(element) {
    if (document.documentElement.getAttribute("data-od-coming-soon") === "1") {
      return true;
    }
    if (!element) return false;

    var scope = element.closest('[data-od-coming-soon]:not([data-od-coming-soon="0"])');
    if (scope && scope !== document.documentElement) return true;

    /* A card from the Coming Soon collection, wherever it is shown. The card
       snippet already works this out from the product's collections and says
       so on the card; the gate simply never read it. */
    return Boolean(element.closest('[data-product-coming-soon="true"]'));
  }

  function readCachedTier() {
    try {
      var raw = sessionStorage.getItem(TIER_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed && Date.now() - parsed.at < TIER_CACHE_TTL_MS) {
        return parsed.tier || "";
      }
    } catch (error) {
      /* private mode, or nothing stored */
    }
    return null;
  }

  function readCachedAccess() {
    try {
      var raw = sessionStorage.getItem(ACCESS_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed && Date.now() - parsed.at < TIER_CACHE_TTL_MS) {
        return parsed.access || null;
      }
    } catch (error) {
      /* private mode, or nothing stored */
    }
    return null;
  }

  function rememberAccess(value) {
    comingSoonAccess = value || null;
    try {
      sessionStorage.setItem(
        ACCESS_CACHE_KEY,
        JSON.stringify({ at: Date.now(), access: comingSoonAccess })
      );
    } catch (error) {
      /* not worth failing over */
    }
  }

  function resolveTier(options) {
    var force = Boolean(options && options.force);

    if (!window.ODCustomerSignedIn) tier = "";

    if (!force) {
      var cachedAccess = readCachedAccess();
      var cached = window.ODCustomerSignedIn ? readCachedTier() : "";

      if (cachedAccess !== null && cached !== null) {
        tier = cached;
        comingSoonAccess = cachedAccess;
        resolvedAt = Date.now();
        return Promise.resolve(tier);
      }
    }

    /* Liquid's copy if it has one, so a signed-in Patron is not gated for the
       moment the request is in flight. */
    if (window.ODMembershipTier) tier = String(window.ODMembershipTier);

    return fetch(TIER_ENDPOINT, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(function (response) { return response.json(); })
      .then(function (payload) {
        tier = (payload && payload.tier) || "";
        rememberAccess(payload && payload.comingSoonAccess);
        resolvedAt = Date.now();
        try {
          sessionStorage.setItem(
            TIER_CACHE_KEY,
            JSON.stringify({ at: Date.now(), tier: tier })
          );
        } catch (error) {
          /* not worth failing over */
        }
        return tier;
      })
      .catch(function () {
        /* Left unknown, which lets the click through. Blocking a Patron
           because a request failed is the worse of the two mistakes, and the
           real entitlement is enforced by the app rather than here. */
        return tier;
      });
  }

  /* The policy's word, not ours. */
  function mayPlayComingSoon() {
    return comingSoonAccess !== "none";
  }

  function buildModal() {
    if (modal) return modal;

    var root = document.createElement("div");
    root.className = "od-csgate";
    root.setAttribute("hidden", "");
    root.innerHTML =
      '<div class="od-csgate__backdrop" data-od-csgate-close></div>' +
      '<div class="od-csgate__panel" role="dialog" aria-modal="true" aria-labelledby="od-csgate-title">' +
      '<button type="button" class="od-csgate__close" aria-label="Close" data-od-csgate-close>&times;</button>' +
      '<p class="od-csgate__eyebrow">Coming Soon</p>' +
      '<h2 class="od-csgate__title" id="od-csgate-title"></h2>' +
      '<p class="od-csgate__body"></p>' +
      /* The SPA transition directive, exactly as the theme's own links carry
         it. Alpine binds it through its mutation observer when this is added to
         the document; the re-init that app.js and the wishlist perform is for a
         case this is not, nodes removed and re-added inside one batch. */
      '<a class="od-csgate__button" href="#" x-element-transition-trigger></a>' +
      "</div>";

    document.body.appendChild(root);

    root.addEventListener("click", function (event) {
      if (event.target.hasAttribute("data-od-csgate-close")) close();
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") close();
    });

    modal = root;
    return root;
  }

  function settings() {
    var html = document.documentElement;
    return {
      title: html.getAttribute("data-od-csgate-title") || "Become a Patron",
      body:
        html.getAttribute("data-od-csgate-body") ||
        "Become a Patron and gain access to this and hundreds of other recordings exclusively available here.",
      label: html.getAttribute("data-od-csgate-label") || "See Patron membership",
      url: html.getAttribute("data-od-csgate-url") || "/pages/patron",
    };
  }

  function open() {
    var root = buildModal();
    var config = settings();

    root.querySelector(".od-csgate__title").textContent = config.title;
    root.querySelector(".od-csgate__body").textContent = config.body;

    var button = root.querySelector(".od-csgate__button");
    button.textContent = config.label;
    button.setAttribute("href", config.url);

    root.hidden = false;
    document.documentElement.classList.add("od-csgate-open");

    /* Read a layout property before adding the class, so the browser has
       committed the un-hidden state and treats what follows as a change to
       animate rather than as the element's starting appearance. */
    void root.offsetWidth;
    root.classList.add("is-open");

    button.focus();
  }

  function close() {
    if (!modal || modal.hidden) return;

    modal.classList.remove("is-open");
    document.documentElement.classList.remove("od-csgate-open");

    /* Hidden only once it has faded, or the dialog would vanish mid-animation.
       A timer rather than transitionend alone: a dialog that never hides
       because an event did not fire would block the whole page. */
    var node = modal;
    window.setTimeout(function () {
      if (!node.classList.contains("is-open")) node.hidden = true;
    }, 220);
  }

  /*
   * Closed when the page changes underneath it.
   *
   * navigateWithTransition calls replaceMainContent, which does not touch
   * body-level nodes, so this dialog would otherwise sit over the page it just
   * sent somebody to. The theme's own popup store would handle that, but
   * showPopup returns early off mobile and this has to work on a desktop.
   *
   * Not from the click: the directive binds its handler to the anchor and calls
   * stopPropagation, so a listener on the dialog never hears it. "pushstate" is
   * dispatched by pushStateAndNotify on every transition, which is the theme's
   * own signal and does not depend on handler order.
   */
  window.addEventListener("pushstate", close);
  window.addEventListener("popstate", close);

  /* Capture phase, so this runs before the player's own document listener and
     can stop the click reaching it. */
  document.addEventListener(
    "click",
    function (event) {
      var target = event.target instanceof Element ? event.target : null;
      var launch = target && target.closest(".opera-player-launch");
      if (!launch) return;

      if (!isComingSoonContext(launch)) return;
      if (mayPlayComingSoon()) return;

      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();

      open();
    },
    true
  );

  /*
   * Becoming a Patron has to take effect without being told to reload.
   *
   * Three ways it can happen, and each needs its own nudge:
   *
   *   - upgrading on the membership page, which clears the shared tier cache
   *     and reloads, so the next page resolves afresh. Already handled, but the
   *     event below closes the gap before the reload lands;
   *   - subscribing through checkout, where the tier is written by a webhook
   *     moments after the customer is already back in the shop. Nothing in the
   *     browser can know when that lands, so the page looks again when it comes
   *     back into view;
   *   - the iOS app, which changes a membership on a different surface
   *     entirely.
   *
   * In every case the worst outcome is a brand new Patron being shown an offer
   * they have just accepted, which is exactly the moment it would rankle.
   */
  function refresh() {
    return resolveTier({ force: true });
  }

  window.ODRefreshMembershipTier = refresh;

  window.addEventListener("od:membership-changed", function (event) {
    /*
     * The event carries a tier, and what a tier may do is the policy's to say,
     * so this asks again rather than working it out from the name. Taking the
     * tier as the answer was what let a Collector past the gate.
     */
    var next = event && event.detail && event.detail.tier;
    if (typeof next === "string") tier = next;

    refresh().then(function () {
      /* If the dialog is open on the page they just upgraded from, it is now
         wrong. */
      if (mayPlayComingSoon()) close();
    });
  });

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState !== "visible") return;
    if (Date.now() - resolvedAt < REFRESH_AFTER_MS) return;
    refresh();
  });

  /* Coming back to a cached page, which a browser can serve without running
     any of this again. */
  window.addEventListener("pageshow", function (event) {
    if (event.persisted) refresh();
  });

  resolveTier();
})();
